import { useEffect } from "react";
import CheckoutButton from "../components/CheckoutButton";
import OrderMetaPanel from "../components/OrderMetaPanel";
import { useCart } from "../state/cart";
import { repriceCartItems } from "../api/pricing";
import toast from "react-hot-toast";

export default function CartPage() {
  const items = useCart((s) => s.items);
  const setQty = useCart((s) => s.setQty);
  const remove = useCart((s) => s.remove);
  const clear = useCart((s) => s.clear);
  const replacePrices = useCart((s) => s.replacePrices);

  const total = items.reduce((s, it) => s + it.price * it.qty, 0);

  useEffect(() => {
    if (!("persist" in useCart) || !useCart.persist?.hasHydrated?.()) {
      const unsub = useCart.persist?.onFinishHydration?.(() => {
        syncPrices(useCart.getState().items);
      });
      return () => unsub && unsub();
    }
    syncPrices(useCart.getState().items);

    async function syncPrices(list: typeof items) {
      if (!list.length) return;
      const { latestById, changed } = await repriceCartItems(list);
      if (latestById.size) {
        replacePrices(latestById);
        if (changed.length) toast("Цены обновлены в корзине.", { icon: "🔄" });
      }
    }
  }, [replacePrices]);

  return (
    <div className="space-y-3 p-3">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold">Корзина</h2>
        {items.length > 0 && (
          <button onClick={clear} className="text-sm underline">
            Очистить
          </button>
        )}
      </div>

      {items.length === 0 && (
        <div className="text-sm text-gray-500">Корзина пуста</div>
      )}

      <div className="space-y-2">
        {items.map((item) => (
          <div
            key={item.id}
            className="border rounded bg-white p-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between"
          >
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold line-clamp-2 sm:line-clamp-1">
                {item.title}
              </div>

              <div className="mt-0.5 text-xs text-gray-600 flex flex-wrap items-center gap-x-2 gap-y-1">
                <span className="whitespace-nowrap">
                  {item.price.toFixed(0)} руб.
                </span>

                {item.taste && (
                  <span className="break-words">
                    Вкус: {item.taste.name}
                    {item.taste.price ? ` (+${item.taste.price} руб.)` : ""}
                  </span>
                )}

                {item.sizeKey && (
                  <span className="whitespace-nowrap">
                    Размер: {item.sizeKey}
                  </span>
                )}
              </div>
            </div>

            <div className="w-full sm:w-auto flex flex-wrap items-center gap-2 sm:justify-end">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setQty(item.id, Math.max(1, item.qty - 1))}
                  className="w-8 h-8 border rounded shrink-0"
                  aria-label="Уменьшить количество"
                >
                  −
                </button>

                <input
                  type="number"
                  min={1}
                  value={item.qty}
                  onChange={(e) =>
                    setQty(item.id, Math.max(1, Number(e.target.value) || 1))
                  }
                  className="w-16 h-8 border rounded text-center text-sm"
                />

                <button
                  onClick={() => setQty(item.id, item.qty + 1)}
                  className="w-8 h-8 border rounded shrink-0"
                  aria-label="Увеличить количество"
                >
                  +
                </button>
              </div>

              <button
                onClick={() => remove(item.id)}
                className="text-xs underline ml-auto sm:ml-2"
              >
                Удалить
              </button>
            </div>
          </div>
        ))}
      </div>

      {items.length > 0 && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 border-t pt-3">
          <div className="text-sm">Итого:</div>
          <div className="text-base font-semibold whitespace-nowrap">
            {total.toFixed(0)} руб.
          </div>
        </div>
      )}

      {items.length > 0 && (
        <div className="space-y-3">
          <OrderMetaPanel maxTables={22} />
          <CheckoutButton />
        </div>
      )}
    </div>
  );
}
